import { Accession, Clinic } from "pwapi";

export const hasErrors = (
  errors: { [key: string]: string | undefined },
  forms?: string[]
) =>
  errors &&
  Object.keys(errors).some(
    (key) => errors[key] !== undefined && (!forms || forms.includes(key))
  );

export const getValueFromObject = (
  obj: Record<string, any>,
  key: string
): any => {
  return key
    .split(".")
    .reduce(
      (acc, part) => (acc && acc[part] !== undefined ? acc[part] : undefined),
      obj
    );
};

export const flattenObjectToArray = (obj: object, parentKey = ""): any[] => {
  return Object.entries(obj).reduce((acc: any[], [key, value]) => {
    const newKey = parentKey ? `${parentKey}.${key}` : key;
    if (typeof value === "object" && value !== null && !Array.isArray(value)) {
      return acc.concat(flattenObjectToArray(value, newKey));
    } else {
      acc.push({ id: newKey, message: value });
      return acc;
    }
  }, []);
};

export const getErrors = (
  errors: Record<string, any> = {},
  touched: Record<string, any> = {},
  touchedFieldsOnly: boolean = false,
  forms: string[] = []
): Record<string, string> => {
  const extractMessages = (
    errorObj: Record<string, any>,
    formPath: string[],
    touchedObj: Record<string, any>,
    fullPath: string
  ): Record<string, string> => {
    if (formPath.length === 0) return {};

    const [currentKey, ...remainingPath] = formPath;

    if (remainingPath.length === 0) {
      return touchedObj?.[currentKey] && errorObj?.[currentKey]
        ? { [fullPath]: errorObj[currentKey] }
        : {};
    }

    // Recursively check nested fields
    if (errorObj?.[currentKey] && touchedObj?.[currentKey]) {
      return extractMessages(
        errorObj[currentKey],
        remainingPath,
        touchedObj[currentKey],
        fullPath
      );
    }

    return {};
  };

  // If forms are provided, extract errors for those form fields only
  // Otherwise, extract errors for all touched fields
  if (forms.length > 0) {
    return forms.reduce((acc, formField) => {
      const formPath = formField.split(".");
      const fieldErrors = extractMessages(errors, formPath, touched, formField);
      return { ...acc, ...fieldErrors };
    }, {});
  } else {
    return Object.entries(errors).reduce(
      (acc: Record<string, any>, [key, value]) => {
        if ((!touchedFieldsOnly || touched[key]) && value) {
          acc[key] = value;
        }
        return acc;
      },
      {} as Record<string, any>
    );
  }
};

export const getValidationWarnings = (warning: any) => {
  const validationWarnings: Record<string, any> = {};

  warning.inner.forEach((w: any) => {
    if (w.path) {
      if (w.path.includes(".")) {
        const [parentKey, childKey] = w.path.split(".");
        validationWarnings[parentKey] = {
          ...(typeof validationWarnings[parentKey] === "object"
            ? validationWarnings[parentKey]
            : {}),
          [childKey]: w.message,
        };
      } else {
        validationWarnings[w.path] = w.message;
      }
    }
  });

  return validationWarnings;
};

export const emrFieldValidation = (
  context: any,
  fieldName: string,
  value: string | null | undefined
) => {
  const clinic: Clinic | null | undefined = context?.options?.context?.clinic;
  const ordering_doctor_id: number | null | undefined =
    context?.options?.context?.ordering_doctor_id ||
    context?.options?.context?.ordering_doctor?.id;
  const skip_provider_ids = clinic?.emr_mappings?.flatMap(
    (em) => em?.settings?.skip_provider_ids || []
  );

  if (context.options?.context?.bill_tc && !context.options?.context?.bill_pc) {
    // Do not require integration field validation on cases with billing component TC only
    return true;
  }

  if (
    context.options?.context?.case_type === Accession.case_type.CONSULT ||
    context.options?.context?.case_type === Accession.case_type.MOHS_QC
  ) {
    // Do not require integration field validation on consult or MOHS QC cases
    return true;
  }

  if (!clinic?.emrs?.length) {
    // If there are no EMRs configured, no validation is needed
    return true;
  }

  if (ordering_doctor_id && skip_provider_ids?.includes(ordering_doctor_id)) {
    // If the Ordering Provider is omitted from EHR/EMR integration, no validation is needed
    return true;
  }

  const field = clinic?.emrs[0]?.fields.find(
    (emr) => emr.integration_attribute === fieldName
  );

  // Return true if the field isn't used in the EMR, or if the field is not required
  if (!field?.required) return true;

  // If the field is required, check if the value is empty
  // If so, build the error message from the field label
  if (!value) {
    return context.createError({
      message: `${field.label} is required`,
    });
  }

  return true;
};

export const requiredError = (error?: string) => error?.includes("is required");

export const canValidate = (customerType: Clinic["customer_type"]): boolean => {
  return customerType !== Clinic.customer_type.NON_DIAGNOSTIC;
};
